<template>
  <div class="offline-tab">
    <div class="tab-header">
      <div class="tab-header-meta">
        <span class="tab-count">{{ employees.length }} {{ employees.length === 1 ? 'funcionário' : 'funcionários' }}</span>
      </div>
      <div v-if="!isAnalyst && !showForm" class="header-actions">
        <button class="btn-new" type="button" @click="$emit('open-form')">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          Novo funcionário
        </button>
      </div>
    </div>

    <template v-if="!showForm">
      <div v-if="employees.length === 0" class="empty-state">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="14" r="7" stroke="var(--ink-muted,#888)" stroke-width="2"/><path d="M6 36c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="var(--ink-muted,#888)" stroke-width="2" stroke-linecap="round"/></svg>
        <p>Nenhum funcionário não logado cadastrado.</p>
        <p class="empty-sub">Cadastre pessoas para controle interno e uso em presenças.</p>
      </div>

      <div v-else class="employee-cards">
        <div v-for="employee in employees" :key="employee.id" class="employee-card">
          <div class="employee-avatar" :style="{ background: avatarColor(employee.name) }">
            <img v-if="employee.photoUrl" :src="$resolveUrl(employee.photoUrl)" alt="foto" class="avatar-img" />
            <span v-else class="avatar-initials">{{ initials(employee.name) }}</span>
          </div>

          <div class="employee-info">
            <div class="employee-name">{{ employee.name }}</div>
            <div class="employee-meta">
              <span class="employee-username">@{{ employee.username }}</span>
              <span v-if="employee.roleName" class="role-chip">{{ employee.roleName }}</span>
            </div>
          </div>

          <div v-if="!isAnalyst" class="employee-actions">
            <button class="action-btn" type="button" title="Editar" @click="$emit('edit-employee', employee)">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M10.5 2.5l2 2-8 8H2.5v-2l8-8z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>
            </button>
            <button class="action-btn action-btn--danger" type="button" title="Apagar" @click="$emit('delete-employee', employee)">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M3 4h9M6 4V2.5h3V4M5.5 4v7.5h4V4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>
    </template>

    <div v-if="showForm && !isAnalyst" class="form-panel">
      <div class="form-panel-header">
        <h3 class="form-panel-title">{{ form.isEditing ? 'Editar funcionário' : 'Novo funcionário não logado' }}</h3>
        <button class="btn-cancel-link" type="button" @click="$emit('close-form')">Cancelar</button>
      </div>

      <fieldset class="form-section">
        <legend class="form-section-label">Identidade</legend>
        <div class="field-row">
          <div class="field">
            <label class="field-label" for="offline-name">Nome</label>
            <input id="offline-name" class="field-input" :value="form.name" type="text" placeholder="Ex: José Pereira" @input="updateField('name', ($event.target as HTMLInputElement).value)" />
          </div>
          <div class="field">
            <label class="field-label" for="offline-username">Username interno</label>
            <input id="offline-username" class="field-input" :value="form.username" type="text" placeholder="josepereira" @input="updateField('username', ($event.target as HTMLInputElement).value)" />
          </div>
        </div>
        <div class="field">
          <label class="field-label" for="offline-role">Cargo</label>
          <select id="offline-role" class="field-input field-select" :value="form.roleId" @change="updateField('roleId', ($event.target as HTMLSelectElement).value)">
            <option value="">Sem cargo</option>
            <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.name }}</option>
          </select>
        </div>
      </fieldset>

      <fieldset class="form-section">
        <legend class="form-section-label">Foto</legend>
        <div class="photo-row">
          <div class="photo-preview" :style="{ background: avatarColor(form.name || form.username || '?') }">
            <img v-if="form.photoUrl" :src="form.photoUrl.startsWith('data:') ? form.photoUrl : $resolveUrl(form.photoUrl)" alt="preview" class="avatar-img" />
            <span v-else class="avatar-initials">{{ initials(form.name || form.username || '?') }}</span>
          </div>
          <div class="photo-actions">
            <button class="btn-ghost-action" type="button" @click="triggerPhotoInput">Escolher foto</button>
            <button v-if="form.photoUrl" class="btn-ghost-action" type="button" @click="updateField('photoUrl', '')">Remover</button>
            <input ref="photoInputRef" class="hidden-input" type="file" accept="image/*" @change="onPhotoSelected" />
          </div>
        </div>
      </fieldset>

      <div class="form-actions">
        <button class="btn-primary-action" type="button" @click="$emit('save-employee')">
          {{ form.isEditing ? 'Salvar alterações' : 'Cadastrar funcionário' }}
        </button>
        <button class="btn-ghost-action" type="button" @click="$emit('close-form')">Cancelar</button>
      </div>
      <p v-if="error" class="field-error form-error">{{ error }}</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, type PropType } from 'vue'
import type { Role } from '@/store/roles'
import type { OfflineEmployee } from '@/store/offlineEmployees'
import { compressImage } from '@/utils/imageCompress'

interface OfflineEmployeeFormModel {
  id: string
  name: string
  username: string
  roleId: string
  photoUrl: string
  isEditing: boolean
}

const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626', '#db2777']

export default defineComponent({
  name: 'UsersOfflineEmployeesTab',
  props: {
    employees: { type: Array as PropType<OfflineEmployee[]>, default: () => [] },
    roles: { type: Array as PropType<Role[]>, default: () => [] },
    showForm: { type: Boolean, default: false },
    form: { type: Object as PropType<OfflineEmployeeFormModel>, required: true },
    error: { type: String, default: '' },
    isAnalyst: { type: Boolean, default: false }
  },
  emits: ['open-form', 'close-form', 'save-employee', 'edit-employee', 'delete-employee', 'update-form'],
  setup(_props, { emit }) {
    const photoInputRef = ref<HTMLInputElement | null>(null)

    const initials = (name: string) => {
      const parts = `${name || '?'}`.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return `${name || '?'}`.slice(0, 2).toUpperCase()
    }

    const avatarColor = (name: string) => {
      const code = `${name || '?'}`.charCodeAt(0) || 0
      return AVATAR_COLORS[code % AVATAR_COLORS.length]
    }

    const updateField = (field: string, value: string) => {
      emit('update-form', { field, value })
    }

    const triggerPhotoInput = () => {
      photoInputRef.value?.click()
    }

    const onPhotoSelected = async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0]
      if (!file) return
      const photoUrl = await compressImage(file, 800, 800)
      emit('update-form', { field: 'photoUrl', value: photoUrl })
      ;(event.target as HTMLInputElement).value = ''
    }

    return {
      photoInputRef,
      initials,
      avatarColor,
      updateField,
      triggerPhotoInput,
      onPhotoSelected
    }
  }
})
</script>

<style scoped lang="scss">
.offline-tab {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.tab-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.tab-count,
.empty-sub {
  color: var(--ink-muted, #6b7280);
}

.header-actions {
  display: flex;
  gap: 10px;
}

.btn-new,
.btn-primary-action,
.btn-ghost-action,
.btn-cancel-link,
.action-btn {
  border: none;
  cursor: pointer;
}

.btn-new,
.btn-primary-action,
.btn-ghost-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 600;
}

.btn-new,
.btn-primary-action {
  background: var(--accent, #2563eb);
  color: #fff;
}

.btn-ghost-action {
  background: rgba(15, 23, 42, 0.06);
  color: var(--ink, #111827);
}

.btn-cancel-link {
  background: transparent;
  color: var(--accent, #2563eb);
  font-weight: 600;
}

.empty-state {
  padding: 42px 24px;
  text-align: center;
  border: 1px dashed rgba(15, 23, 42, 0.12);
  border-radius: 18px;
}

.employee-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
}

.employee-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 18px;
  background: var(--card, #fff);
}

.employee-avatar,
.photo-preview {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.employee-info {
  flex: 1;
  min-width: 0;
}

.employee-name {
  font-weight: 700;
  color: var(--ink, #111827);
}

.employee-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: var(--ink-muted, #6b7280);
  font-size: 13px;
}

.role-chip {
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
  font-size: 11px;
  font-weight: 700;
}

.employee-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.06);
  color: var(--ink, #111827);
}

.action-btn--danger {
  color: #dc2626;
}

.form-panel {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 20px;
  background: var(--card, #fff);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-panel-header,
.field-row,
.photo-row,
.form-actions {
  display: flex;
  gap: 14px;
}

.form-panel-header,
.photo-row,
.form-actions {
  align-items: center;
  justify-content: space-between;
}

.form-section {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  padding: 16px;
}

.form-section-label,
.field-label {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-muted, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.field-input {
  width: 100%;
  min-width: 0;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  padding: 11px 13px;
  background: #fff;
}

.photo-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.field-error {
  color: #dc2626;
  font-size: 13px;
  margin: 0;
}

.hidden-input {
  display: none;
}

@media (max-width: 720px) {
  .employee-card,
  .field-row,
  .photo-row,
  .form-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .employee-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
